import React from "react";
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";
import { NextSeo, SocialProfileJsonLd } from "next-seo";
import { useReactiveVar } from "@apollo/client";

import { DEFAULT_BOOK_ID } from "src/common-services";
import { DEFAULT_SPORT, SPORTS_MAP, WEB_HEADER_SPORTS_NAME_LIST } from "src/constants";
import { getSEOTitle, getSEODescription } from "src/components/SEO";
import LiveGameBar from "src/components/LiveGameBar";
import { SportGamesConnector, useGamesSportIndexData } from "src/components/SportGames";
import styles from "src/styles/sport-index.scss";
import Surveys from "src/components/Surveys";
import TileNav from "src/components/TileNav";
import { LatestArticles } from "src/components/ArticleGrid";
import Hero from "src/components/Hero";
import FeaturedTrends from "src/components/FeaturedTrends";
import { useIndexArticles } from "src/services/FeaturedContent";
import { addApolloState, initializeApollo } from "src/lib/apollo-client";
import { getGameIndexVars } from "src/components/GamesIndex";
import { INDEX_EVENTS, INFINITE_ARTICLES_QUERY, LEAGUE_CONFIG_QUERY, SPORT_INDEX_EVENTS } from "src/gql/queries";
import { getDefaultAfterDateRange, getDefaultBeforeDateRange } from "src/services/DateService";
import { currentSport } from "src/services/ApolloService";
import { ScheduleItem, getSchedule } from "src/services/ScheduleService";

interface SportIndexParams {
  leagueUrl: string;
  sportSchedule: ScheduleItem[];
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: WEB_HEADER_SPORTS_NAME_LIST.map((sport) => ({ params: { sport } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<SportIndexParams> = async (ctx) => {
  const { params, previewData } = ctx;
  const leagueUrl = params?.sport as string;
  const league = SPORTS_MAP[leagueUrl];
  if (!league) {
    return {
      notFound: true,
    };
  }
  currentSport(league);
  const apolloClient = initializeApollo();
  let context: any = {};
  if (previewData) {
    context.headers = {
      "Prismic-ref": (previewData as { ref: string })?.ref,
    };
  }
  // Load query results into apollo cache for hydration
  await apolloClient.query({
    query: LEAGUE_CONFIG_QUERY,
    variables: {
      league: leagueUrl ?? "none",
      includeSurveyResponses: false,
    },
    context,
  });
  await apolloClient.query({
    query: INFINITE_ARTICLES_QUERY,
    variables: {
      first: 9,
      where: {
        hidden: false,
        league: leagueUrl ?? undefined,
      },
    },
    context,
  });
  await apolloClient.query({
    query: INDEX_EVENTS,
    variables: await getGameIndexVars(),
    context,
  });
  const sportSchedule = await getSchedule(league.lookupKey);
  await apolloClient.query({
    query: SPORT_INDEX_EVENTS,
    variables: {
      league: league.key,
      after: getDefaultAfterDateRange(league.lookupKey, sportSchedule),
      before: getDefaultBeforeDateRange(league.lookupKey, sportSchedule),
      bookId: DEFAULT_BOOK_ID, // Consensus
    },
  });
  return addApolloState(apolloClient, {
    props: { leagueUrl, sportSchedule },
    revalidate: 10 * 60, // 10 mins
  });
};

export default function SportIndex({ leagueUrl, sportSchedule }: InferGetStaticPropsType<typeof getStaticProps>) {
  const league = useReactiveVar(currentSport) ?? DEFAULT_SPORT;
  const articles = useIndexArticles({ leagueUrl });
  const games = useGamesSportIndexData(sportSchedule);

  const pageTitle = getSEOTitle(
    leagueUrl,
    undefined,
    null,
    null,
    "BetQL | Sports Betting Picks, Predictions, and Articles"
  );

  const pageDescription = getSEODescription(leagueUrl, undefined, null, null);

  return (
    <React.Fragment>
      <LiveGameBar sport={league} />
      <div
        className="page-title"
        style={{
          backgroundImage: `linear-gradient(0deg, #00214D88, #00214D88), url(/heros/${league.key}.jpg)`,
        }}
      >
        <h1 className="page-title-h1">{league.name}</h1>
        {!!league.h1Description && <div className="page-title-description">{league.h1Description}</div>}
      </div>
      <div className="sport-home-page">
        <NextSeo
          title={pageTitle}
          description={pageDescription}
          openGraph={{
            url: `https://www.betql.co/${leagueUrl}`,
            title: pageTitle,
            description: pageDescription,
          }}
        />
        <SocialProfileJsonLd
          type="Organization"
          name="BetQL"
          url="https://betql.co"
          sameAs={[
            "https://www.facebook.com/betql/",
            "https://www.instagram.com/betqlapp",
            "https://www.linkedin.com/company/rotoql",
            "https://twitter.com/betqlapp",
          ]}
        />
        <TileNav />
        <LatestArticles articles={articles} />
        <SportGamesConnector games={games} />
        <div className="wide-container">
          <div className="wide">
            <Hero />
            <FeaturedTrends />
          </div>
        </div>
        <div>
          <Surveys header={<h2 className="heading">Latest Polls</h2>} />
        </div>
      </div>
      <style jsx global>
        {styles}
      </style>
    </React.Fragment>
  );
}
